import React, { useState, useEffect, useRef } from "react";
import QuestionItem from "../components/QuestionItem";
import QuestionsAnswersArray from "../components/QuestionsAnswersArray";
import { CButton, CCollapse } from "@coreui/react";
import useQuestions from "../hooks/useQuestions";
import { ReactComponent as PlusIcon } from "../assets/images/icons/plus.svg";
import { ReactComponent as BinIcon } from "../assets/images/icons/bin.svg";
import { ReactComponent as CopyIcon } from "../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../assets/images/icons/copy.svg";
import { Tooltip } from "react-tooltip";
import { FormArray, FormBuilder } from "react-reactive-form";
import useModalWindow from "../hooks/useModalWindow";
import { toast } from "react-toastify";
import {
  calculateInitialPdBlockIds,
  addPdBlock,
  removePdBlock,
  duplicatePdBlock,
} from "./utils/pdUtils";

const StandardPDQuestions = ({
  globalId,
  wizardForm,
  // questionsData,
}) => {
  const [pdBlockIds, setPdBlockIds] = useState(() =>
    calculateInitialPdBlockIds(wizardForm)
  );
  const nextBlockIdRef = useRef(Math.max(...pdBlockIds, 0) + 1);
  const lastBlockRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const { questionsDataSaved } = useQuestions();
  const { modalWindow } = useModalWindow();
  const [openBlockIds, setOpenBlockIds] = useState(
    new Set([pdBlockIds[pdBlockIds.length - 1]])
  );

  const dash = "";

  // Sync block IDs when form is restored
  useEffect(() => {
    const newIds = calculateInitialPdBlockIds(wizardForm);
    const currentKey = pdBlockIds.join(",");
    const newKey = newIds.join(",");
    if (newKey !== currentKey) {
      setPdBlockIds(newIds);
      nextBlockIdRef.current = Math.max(...newIds, 0) + 1;
    }
  }, [
    wizardForm.value.qPdStdAnalyses,
    wizardForm.value.qPdAnalyses,
    wizardForm.controls,
  ]);

  // Ensure controls exist for each block
  useEffect(() => {
    const isPdEnabled =
      wizardForm.controls.qPdStdEnabled?.value === "true" ||
      questionsDataSaved?.wizardForm?.qPdStdEnabled === "true";
    if (isPdEnabled) {
      pdBlockIds.forEach(blockId => {
        const suffix = blockId > 1 ? `_${blockId}` : "";

        const controlsToEnsure = [
          { name: `qPdTable${suffix}`, defaultValue: "" },
          { name: `qPdPopulation${suffix}`, defaultValue: "" },
          { name: `qPdColumnHeaders${suffix}`, defaultValue: [] },
          { name: `qPdTimepoints${suffix}`, defaultValue: [] },
        ];

        controlsToEnsure.forEach(({ name, defaultValue }) => {
          if (!wizardForm.contains(name)) {
            if (
              name.startsWith("qPdColumnHeaders") ||
              name.startsWith("qPdTimepoints")
            ) {
              wizardForm.addControl(name, new FormArray([]));
            } else {
              wizardForm.addControl(name, FormBuilder.control(defaultValue));
            }
          }
        });
      });
    }
  }, [wizardForm, pdBlockIds, questionsDataSaved?.wizardForm?.qPdStdEnabled]);

  useEffect(() => {
    if (shouldScrollRef.current && lastBlockRef.current) {
      const el = lastBlockRef.current;
      let wrapper = el.parentElement;
      while (
        wrapper &&
        !wrapper.classList.contains("dashboard-content__wrapper")
      ) {
        wrapper = wrapper.parentElement;
      }
      if (wrapper) {
        const elTop = el.getBoundingClientRect().top;
        const wrapperTop = wrapper.getBoundingClientRect().top;
        wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
      }
      shouldScrollRef.current = false;
    }
  }, [pdBlockIds.length]);

  const handleAddBlock = () => {
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    addPdBlock(wizardForm, newId);
    shouldScrollRef.current = true;
    setPdBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
  };

  const handleDuplicateBlock = (e, displayIndex) => {
    if (e) e.preventDefault();
    const sourceBlockId = pdBlockIds[displayIndex];
    if (sourceBlockId === undefined) return;
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    duplicatePdBlock(wizardForm, sourceBlockId, newId);
    shouldScrollRef.current = true;
    setPdBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
    toast.success("Block duplicated successfully");
  };

  const handleDuplicateLastBlock = e => {
    if (e) e.preventDefault();
    if (pdBlockIds.length === 0) return;
    handleDuplicateBlock(e, pdBlockIds.length - 1);
  };

  const handleRemoveBlock = async (e, displayIndex) => {
    if (e) e.preventDefault();
    if (pdBlockIds.length <= 1) return;
    const confirmed = await modalWindow({
      title: "Remove Block",
      message: "Do you want to remove this block of questions?",
      confirmButtonLabel: "Yes",
    });
    if (!confirmed) return;
    const blockId = pdBlockIds[displayIndex];
    removePdBlock(wizardForm, blockId);
    const remainingIds = pdBlockIds.filter((_, i) => i !== displayIndex);
    setPdBlockIds(remainingIds);
    setOpenBlockIds(new Set([remainingIds[remainingIds.length - 1]]));
  };

  const renderBlockContent = (blockId, displayIndex) => {
    const suffix = blockId > 1 ? `_${blockId}` : "";
    const isFirstBlock = displayIndex === 0;
    const blockNumber = displayIndex + 1;

    const q = n => (isFirstBlock ? `${n}` : `${n}${dash}(${blockNumber})`);
    const questionIndex1a = q(2);
    const questionIndex1b = q(3);
    const questionIndex1c = q(4);
    const questionIndex1d = q(5);

    return (
      <>
        {wizardForm.controls.qPdStdEnabled?.value === "true" && (
          <div
            style={{
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
            }}>
            <CButton
              color={"primary"}
              shape={"pill"}
              data-tooltip-id={`duplicate-std-pd-block-${blockId}`}
              style={{
                border: "1px solid #006dee",
                background: "#006dee",
                fontSize: "15px",
                color: "#fff",
                boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
              }}
              onClick={e => handleDuplicateBlock(e, displayIndex)}>
              <CopyIcon /> Duplicate
            </CButton>
            <Tooltip
              id={`duplicate-std-pd-block-${blockId}`}
              place="top"
              delayShow={100}
              className="custom-tooltip">
              Duplicate this block
            </Tooltip>
            {!isFirstBlock && (
              <>
                <CButton
                  color={"danger"}
                  shape={"pill"}
                  data-tooltip-id={`remove-std-pd-block-${blockId}`}
                  onClick={e => handleRemoveBlock(e, displayIndex)}>
                  <BinIcon />
                </CButton>
                <Tooltip
                  id={`remove-std-pd-block-${blockId}`}
                  place="top"
                  delayShow={100}
                  className="custom-tooltip">
                  Remove this block
                </Tooltip>
              </>
            )}
          </div>
        )}

        <QuestionItem
          questionIndex={questionIndex1a}
          questionName={`qPdTable${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPdStdEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainSingleSelectDropdown={true}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1b}
          questionName={`qPdPopulation${suffix}`}
          suffix={suffix}
          activator={wizardForm.controls.qPdStdEnabled?.value === "true"}
          questionInputType={"text"}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1c}
          controlName={`qPdColumnHeaders${suffix}`}
          questionsName={"qPdColumnHeaders"}
          activator={wizardForm.controls.qPdStdEnabled?.value === "true"}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "richtext"]]}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1d}
          controlName={`qPdTimepoints${suffix}`}
          questionsName={"qPdTimepoints"}
          activator={wizardForm.controls.qPdStdEnabled?.value === "true"}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "text"]]}
          globalId={globalId}
        />
      </>
    );
  };

  return (
    <>
      <QuestionItem
        questionIndex={"1"}
        questionName={"qPdStdEnabled"}
        activator={"true"}
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />

      {wizardForm.controls.qPdStdEnabled?.value === "true" && (
        <>
          {pdBlockIds.length === 1 ? (
            <div
              ref={lastBlockRef}
              className="analysis-block"
              //style={{ marginBottom: "2rem" }}
            >
              {renderBlockContent(pdBlockIds[0], 0)}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                }}>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set(pdBlockIds))}>
                  Expand all
                </CButton>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set())}>
                  Collapse all
                </CButton>
              </div>
              <div className="accordion">
                {pdBlockIds.map((blockId, displayIndex) => {
                  const isOpen = openBlockIds.has(blockId);
                  return (
                    <div
                      key={`pd-std-block-${blockId}`}
                      className="accordion-item"
                      ref={
                        displayIndex === pdBlockIds.length - 1
                          ? lastBlockRef
                          : null
                      }>
                      <div className="accordion-header">
                        <button
                          type="button"
                          className={`accordion-button${isOpen ? "" : " collapsed"}`}
                          onClick={() =>
                            setOpenBlockIds(prev => {
                              const next = new Set(prev);
                              next.has(blockId)
                                ? next.delete(blockId)
                                : next.add(blockId);
                              return next;
                            })
                          }>
                          Block {displayIndex + 1}
                        </button>
                      </div>
                      <CCollapse
                        visible={isOpen}
                        className="accordion-collapse">
                        <div className="accordion-body">
                          <div
                            className="analysis-block"
                            //style={{ marginBottom: "2rem" }}
                          >
                            {renderBlockContent(blockId, displayIndex)}
                          </div>
                        </div>
                      </CCollapse>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {wizardForm.controls.qPdStdEnabled?.value === "true" && (
            <div
              style={{
                marginTop: "1rem",
                marginBottom: "2rem",
                display: "flex",
                gap: "0.5rem",
              }}>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleAddBlock}>
                Add another Standard PD analysis <PlusIcon />
              </CButton>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleDuplicateLastBlock}>
                Duplicate block above <CopyOutlineIcon />
              </CButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default StandardPDQuestions;
